/**
 * APIService - Centralized API communication
 * Handles all API calls with dynamic base URL resolution
 */

/**
 * Get the base URL for API calls
 * @returns {string} The base URL for API endpoints
 */
export const getApiBaseUrl = () => {
  const { protocol, hostname, port } = window.location;
  
  // If accessing via ngrok or other proxy (no port in URL), don't add :8080
  // ngrok URLs look like: https://abc123.ngrok.io (no port)
  // localhost looks like: http://localhost:3000 or served from :8080
  if (hostname.includes('ngrok')) {
    // Use current origin without adding port
    return `${protocol}//${hostname}`;
  }
  
  if (hostname.includes('innovationworks')) {
    return `${protocol}//innovationworks.com:49080`;
  }
  
  // For localhost development (React runs on 3000, backend on 8080)
  return `${protocol}//${hostname}:49080`;
};

/**
 * Make a fetch request to an API endpoint with the correct base URL
 * @param {string} endpoint - The API endpoint (e.g., '/api/disability-profiles')
 * @param {RequestInit} options - Fetch options
 * @returns {Promise<Response>} The fetch response
 */
export const apiFetch = async (endpoint, options = {}) => {
  const baseUrl = getApiBaseUrl();
  const url = endpoint.startsWith('/') ? `${baseUrl}${endpoint}` : `${baseUrl}/${endpoint}`;
  
  // Debug logging
  console.log(`🌐 API Request: ${url}`);
  
  return fetch(url, {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  });
};

/**
 * API endpoints for common operations
 */
export const APIEndpoints = {
  // Configuration endpoints
  getConfiguration: (configId) => `/api/ui-configurations/${configId}`,
  getAllConfigurations: () => '/api/ui-configurations',
  
  // Vehicle features
  getVehicleFeatures: () => '/api/vehicle-hmi-features',
  
  // Disability profiles
  getDisabilityProfiles: () => '/api/disability-profiles',
  
  // Server info
  getServerInfo: () => '/api/server-info',
  
  // QR codes
  getQRCode: (configId, size = 300) => `/api/ui-configurations/${configId}/qr-code?size=${size}`,
};

/**
 * Convenience methods for common API calls
 */
export const APIService = {
  /**
   * Fetch a UI configuration by ID
   */
  async fetchConfiguration(configId) {
    const response = await apiFetch(APIEndpoints.getConfiguration(configId));
    if (!response.ok) {
      throw new Error(`Configuration ${configId} not found: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch all UI configurations
   */
  async fetchAllConfigurations() {
    const response = await apiFetch(APIEndpoints.getAllConfigurations());
    if (!response.ok) {
      throw new Error(`Failed to fetch configurations: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch vehicle HMI features
   */
  async fetchVehicleFeatures() {
    const response = await apiFetch(APIEndpoints.getVehicleFeatures());
    if (!response.ok) {
      throw new Error(`Failed to fetch vehicle features: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch disability profiles
   */
  async fetchDisabilityProfiles() {
    const response = await apiFetch(APIEndpoints.getDisabilityProfiles());
    if (!response.ok) {
      throw new Error(`Failed to fetch disability profiles: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Fetch server information
   */
  async fetchServerInfo() {
    const response = await apiFetch(APIEndpoints.getServerInfo());
    if (!response.ok) {
      throw new Error(`Failed to fetch server info: ${response.status}`);
    }
    return response.json();
  },

  /**
   * Direct apiFetch for custom endpoints
   */
  fetch: apiFetch,

  /**
   * Get the API base URL
   */
  getBaseUrl: getApiBaseUrl
};